/**
 * Super-admin v2 — ResetPasswordModal.
 * 3 modes: email_link / new_password / pin.
 */
import { useState, type ReactNode } from "react";
import { superAdminResetOwnerCredentials, type ResetMethod } from "@cafe-os/db";
import { C, FONT, inputStyle, btnPrimary, btnSecondary } from "../styles/theme";
import { ModalWrapper, FormField } from "./ui";
import { KeyRound, Mail, Lock, Hash, Dices, AlertTriangle, CheckCircle2 } from "lucide-react";

interface Props {
  tenantId: string;
  tenantName: string;
  staffId: string;
  onClose: () => void;
}

function genPassword(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let pw = "";
  for (let i = 0; i < 10; i++) pw += chars[Math.floor(Math.random() * chars.length)];
  return pw;
}

function genPin(): string {
  return String(Math.floor(1000 + Math.random() * 9000));
}

export function ResetPasswordModal({ tenantId, tenantName, staffId, onClose }: Props) {
  const [method, setMethod] = useState<ResetMethod>("email_link");
  const [newPassword, setNewPassword] = useState("");
  const [newPin, setNewPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function handleSubmit() {
    setSubmitting(true);
    setErr(null);
    setResult(null);
    try {
      const res = await superAdminResetOwnerCredentials({
        tenantId,
        staffId,
        method,
        newPassword: method === "new_password" ? newPassword : undefined,
        newPin: method === "pin" ? newPin : undefined,
      });
      const msg =
        method === "email_link" ? `Ссылка отправлена. Открыть: ${res.action_link ?? "—"}`
        : method === "new_password" ? "Пароль обновлён. Старые сессии завершены."
        : "PIN обновлён. Старые сессии завершены.";
      setResult(msg);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalWrapper onClose={onClose}>
      <div style={{ padding: 32 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: FONT.display, fontSize: 22, fontWeight: 700, marginBottom: 8, color: C.text }}>
          <KeyRound size={20} /> Сброс доступа
        </div>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 24 }}>
          Кафе: <strong style={{ color: C.text }}>{tenantName}</strong> · Staff:{" "}
          <span style={{ fontFamily: FONT.mono, color: C.text }}>{staffId}</span>
        </div>

        <div style={{
          fontSize: 11, color: C.muted, letterSpacing: 1,
          textTransform: "uppercase", fontWeight: 700, marginBottom: 10,
        }}>Способ сброса</div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
          {([
            { id: "email_link" as const,   icon: <Mail size={22} />, label: "Отправить ссылку на email", desc: "Владелец сам создаст новый пароль" },
            { id: "new_password" as const, icon: <Lock size={22} />, label: "Задать новый пароль",       desc: "Установить пароль вручную" },
            { id: "pin" as const,          icon: <Hash size={22} />, label: "Сбросить PIN-код",          desc: "Для быстрого входа на кассе" },
          ] as { id: ResetMethod; icon: ReactNode; label: string; desc: string }[]).map((m) => (
            <button key={m.id} onClick={() => setMethod(m.id)} style={{
              background: method === m.id ? C.brandSoft : C.bgSoft,
              border: method === m.id ? `2px solid ${C.brand}` : `1px solid ${C.border}`,
              borderRadius: 10, padding: 14,
              cursor: "pointer", textAlign: "left",
              display: "flex", alignItems: "center", gap: 12,
            }}>
              <span style={{ display: "flex", alignItems: "center", color: method === m.id ? C.brand : C.text }}>{m.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{m.label}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{m.desc}</div>
              </div>
            </button>
          ))}
        </div>

        {method === "new_password" && (
          <FormField label="Новый пароль (min 6 chars)">
            <div style={{ display: "flex", gap: 8 }}>
              <input type="text" value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Сгенерируйте..."
                style={{ ...inputStyle(), fontFamily: FONT.mono }} />
              <button onClick={() => setNewPassword(genPassword())} style={{
                display: "flex", alignItems: "center", gap: 6,
                background: C.brandSoft, color: C.brand, border: `1px solid ${C.brand}`,
                padding: "0 16px", borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: "pointer",
              }}><Dices size={14} /> Создать</button>
            </div>
          </FormField>
        )}

        {method === "pin" && (
          <FormField label="Новый PIN (4 цифры)">
            <div style={{ display: "flex", gap: 8 }}>
              <input type="text" value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                maxLength={4} placeholder="0000"
                style={{
                  ...inputStyle(),
                  fontFamily: FONT.mono,
                  fontSize: 24, letterSpacing: 8, textAlign: "center",
                }} />
              <button onClick={() => setNewPin(genPin())} aria-label="Сгенерировать PIN" style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                background: C.brandSoft, color: C.brand, border: `1px solid ${C.brand}`,
                padding: "0 16px", borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: "pointer",
              }}><Dices size={16} /></button>
            </div>
          </FormField>
        )}

        <div style={{
          background: C.orangeSoft, border: `1px solid ${C.orange}30`,
          borderRadius: 10, padding: 12, marginTop: 16,
          display: "flex", gap: 10,
        }}>
          <AlertTriangle size={18} style={{ flexShrink: 0, color: C.orange }} />
          <div style={{ fontSize: 11, color: C.text, lineHeight: 1.5 }}>
            После сброса все активные сессии этого владельца закроются.
            При <strong>email_link</strong> владелец получит письмо от Supabase Auth.
          </div>
        </div>

        {err && (
          <div style={{
            display: "flex", alignItems: "flex-start", gap: 6,
            marginTop: 12, padding: 12,
            background: C.redSoft, border: `1px solid ${C.red}40`,
            borderRadius: 10, color: C.red, fontSize: 12,
          }}><AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} /><span>{err}</span></div>
        )}
        {result && (
          <div style={{
            display: "flex", alignItems: "flex-start", gap: 6,
            marginTop: 12, padding: 12,
            background: C.greenSoft, border: `1px solid ${C.green}40`,
            borderRadius: 10, color: C.green, fontSize: 12, wordBreak: "break-all",
          }}><CheckCircle2 size={14} style={{ flexShrink: 0, marginTop: 1 }} /><span>{result}</span></div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>
          <button onClick={onClose} style={btnSecondary()}>Закрыть</button>
          <button onClick={() => void handleSubmit()} disabled={submitting}
            style={{ ...btnPrimary(submitting), display: "inline-flex", alignItems: "center", gap: 6 }}>
            {submitting ? "Сброс..." : <><CheckCircle2 size={16} /> Сбросить</>}
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
}
